import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import styles from './DatenschutzInfoPopover.module.css';

const NOTICE_TEXT_BEFORE =
  'Wir verarbeiten deine Angaben zur Bearbeitung deiner Anfrage und zur Kontaktaufnahme. Wenn du einen Empfehler angibst, nutzen wir die angegebenen Daten nur zur internen Zuordnung der Empfehlung. Weitere Informationen findest du in unserer ';

export function DatenschutzInfoPopover({
  triggerClassName = '',
  placement = 'above',
  triggerText = 'Mehr zum Datenschutz',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const popoverRef = useRef(null);
  const [layerStyle, setLayerStyle] = useState(null);

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const r = root.getBoundingClientRect();
    if (placement === 'below') {
      setLayerStyle({
        left: r.left + r.width / 2,
        top: r.bottom,
        transform: 'translateX(-50%)',
      });
    } else {
      setLayerStyle({
        left: r.left + r.width / 2,
        top: r.top,
        transform: 'translate(-50%, -100%)',
      });
    }
  }, [placement]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      const t = e.target;
      if (
        rootRef.current?.contains(t) ||
        popoverRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const layer =
    open &&
    layerStyle &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        ref={popoverRef}
        className={`${styles.popover} ${styles.popoverLayer}`.trim()}
        style={layerStyle}
        role="dialog"
        aria-label="Hinweis zur Datenverarbeitung"
      >
        <p className={styles.bodyText}>
          {NOTICE_TEXT_BEFORE}
          <Link to="/datenschutz" className={styles.policyLink} onClick={close}>
            Datenschutzerklärung
          </Link>
          .
        </p>
      </div>,
      document.body
    );

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={`${styles.triggerButton} ${triggerClassName}`.trim()}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        {triggerText}
      </button>
      {layer}
    </div>
  );
}
